import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CardMedia,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Wrapper from "../../../components/Wrapper";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import title from "../../../components/title";

// Icons
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import PhoneIcon from "@mui/icons-material/Phone";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BadgeIcon from "@mui/icons-material/Badge";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DrawIcon from "@mui/icons-material/Draw";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DescriptionIcon from "@mui/icons-material/Description";

import { ProfileRegister } from "../../../redux/reducer/Profile";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import SignatureCapture from "../../../components/SignatureCapture";
import InstallAppCard from "../../../components/InstallAppCard";
import {
  profileSchema,
  occupationOptions,
  monthlyIncomeOptions,
  getSourceOfIncomeOptionsFor,
} from "./validationSchema";
import { useFormik } from "formik";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: "1px solid #eee",
}));

const LockedField = styled(Box)(({ theme }) => ({
  position: "relative",
  "& .MuiTextField-root": {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const LockBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: -8,
  backgroundColor: "#ff9800",
  borderRadius: "50%",
  padding: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
}));

const PhotoUploadBox = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  borderRadius: "50%",
  border: "3px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.04)",
  },
}));

const DocumentCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
}));

// Image Modal Component
function ImageModal({ open, onClose, imageUrl, title }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (!open) {
      setZoom(1);
      setRotation(0);
    }
  }, [open]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#000",
          color: "#fff",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Typography variant="h6" color="white">
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={handleZoomOut} sx={{ color: "white" }}>
            <ZoomOutIcon />
          </IconButton>
          <IconButton onClick={handleZoomIn} sx={{ color: "white" }}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleRotate} sx={{ color: "white" }}>
            <RotateRightIcon />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          p: 3,
        }}
      >
        <img
          src={imageUrl}
          alt={title}
          style={{
            maxWidth: "100%",
            maxHeight: "70vh",
            objectFit: "contain",
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: "transform 0.3s ease",
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function Profile() {
  title("Owner Profile");
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const profileLoading = useSelector((state) => state?.profile?.loading);
  const user = validateUser?.UserValidate?.data?.user;

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  // New states for CNIC and signature uploads
  const [cnicFrontFile, setCnicFrontFile] = useState(null);
  const [cnicBackFile, setCnicBackFile] = useState(null);
  const [signature, setSignature] = useState("");
  const cnicFrontInputRef = useRef(null);
  const cnicBackInputRef = useRef(null);

  // Image Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState({ url: "", title: "" });

  const [originalData, setOriginalData] = useState({});

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ownerName: "",
      fatherName: "",
      cnic: "",
      contactNumber: "",
      alternateNumber: "",
      currentAddress: "",
      permanentAddress: "",
      occupation: "",
      sourceOfIncome: "",
      monthlyIncome: "",
    },
    validationSchema: profileSchema,
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  const handleOccupationChange = (e) => {
    formik.handleChange(e);
    const allowedValues = getSourceOfIncomeOptionsFor(e.target.value).map(
      (opt) => opt.value
    );
    if (
      formik.values.sourceOfIncome &&
      !allowedValues.includes(formik.values.sourceOfIncome)
    ) {
      formik.setFieldValue("sourceOfIncome", "");
    }
  };

  // Initialize user data
  console.log(user, "uuuuuuuuuuuuuuuuu");
  useEffect(() => {
    if (user) {
      const formattedValues = {
        ownerName: user?.name || "",
        fatherName: user?.fatherName || "",
        cnic: user?.cnic || "",
        contactNumber: user?.contactNumber?.replace("03", "") || "",
        alternateNumber: user?.alternateNumber?.replace("03", "") || "",
        currentAddress: user?.currentAddress || "",
        permanentAddress: user?.permanentAddress || "",
        occupation: user?.occupation || "",
        sourceOfIncome: user?.sourceOfIncome || "",
        monthlyIncome: user?.monthlyIncome || "",
      };
      formik.setValues(formattedValues);
      setOriginalData(formattedValues);

      if (user?.profilePhoto || user?.profileImage) {
        setProfileImage(user.profilePhoto || user.profileImage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleImageUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select a valid image", { position: "top-center" });
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB", {
        position: "top-center",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImage(reader.result);
    };
    reader.readAsDataURL(selectedFile);
    setFile(selectedFile);
  };

  const handlePhoneChange = (fieldName) => (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 9) value = value.slice(0, 9);
    formik.setFieldValue(fieldName, value);
  };

  const handleCnicChange = (fieldName) => (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 13) value = value.slice(0, 13);

    let formatted = value;
    if (value.length > 5) {
      formatted = value.slice(0, 5) + "-" + value.slice(5);
    }
    if (value.length > 12) {
      formatted = formatted.slice(0, 13) + "-" + formatted.slice(13);
    }

    formik.setFieldValue(fieldName, formatted);
  };

  const handleImageClick = (url, title) => {
    if (url) {
      setModalImage({ url, title });
      setModalOpen(true);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);

    try {
      const formData = new FormData();

      formData.append("contactNumber", `03${values.contactNumber}`);
      formData.append(
        "alternateNumber",
        values.alternateNumber ? `03${values.alternateNumber}` : ""
      );
      formData.append("ownerName", values.ownerName);
      formData.append("fatherName", values.fatherName);
      formData.append("cnic", values.cnic);
      formData.append("currentAddress", values.currentAddress);
      formData.append("permanentAddress", values.permanentAddress);
      formData.append("occupation", values.occupation);
      formData.append("sourceOfIncome", values.sourceOfIncome);
      formData.append("monthlyIncome", values.monthlyIncome || "");

      // Append images as binary files
      if (file) {
        formData.append("profilePhoto", file);
      }
      if (cnicFrontFile) {
        formData.append("cnicFront", cnicFrontFile);
      }
      if (cnicBackFile) {
        formData.append("cnicBack", cnicBackFile);
      }

      // Append signature as base64 string
      if (signature) {
        formData.append("signature", signature);
      }

      const res = await dispatch(ProfileRegister(formData));

      if (res?.payload?.data?.success) {
        toast.success("Profile updated successfully!", {
          position: "top-center",
        });
        setOriginalData(values);
        setIsEditing(false);
        setFile(null);
        setCnicFrontFile(null);
        setCnicBackFile(null);
        setSignature("");
        dispatch(ValidateUser({}));
      } else {
        toast.error(
          res?.payload?.data?.message || "Failed to update profile",
          {
            position: "top-center",
          }
        );
      }
    } catch (error) {
      toast.error("Failed to update profile", { position: "top-center" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    formik.setValues(originalData);
    setProfileImage(user?.profilePhoto || user?.profileImage || null);
    setFile(null);
    setCnicFrontFile(null);
    setCnicBackFile(null);
    setSignature("");
    setIsEditing(false);
  };

  if (profileLoading) {
    return (
      <>
        <Wrapper />
        <div
          className={`main-content ${open ? "sidebar-open" : "sidebar-closed"}`}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        </div>
      </>
    );
  }

  return (
    <>
      <Wrapper />
      <div
        className={`main-content ${open ? "sidebar-open" : "sidebar-closed"}`}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="primary">
              Owner Profile
            </Typography>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                sx={{ bgcolor: "black", "&:hover": { bgcolor: "#333" } }}
              >
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={formik.handleSubmit}
                  disabled={saving}
                  sx={{ bgcolor: "black", "&:hover": { bgcolor: "#333" } }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            )}
          </Box>

          {/* Identity Information - Locked */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <VerifiedUserIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Identity Information
                </Typography>
                <Chip
                  label="Verified & Locked"
                  size="small"
                  color="warning"
                  icon={<LockIcon sx={{ fontSize: 14 }} />}
                />
                <Typography
                  variant="p"
                  fontWeight={400}
                  sx={{
                    marginLeft: "auto",
                    fontStyle: "italic",
                    color: "#00000066",
                  }}
                >
                  Email: {user?.email}
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                {/* Profile Photo */}
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <PhotoUploadBox
                      onClick={() => isEditing && fileInputRef.current?.click()}
                    >
                      {profileImage ? (
                        <Avatar
                          src={profileImage}
                          sx={{ width: "100%", height: "100%" }}
                        />
                      ) : (
                        <Box sx={{ textAlign: "center", color: "#999" }}>
                          <CameraAltIcon sx={{ fontSize: 32 }} />
                          <Typography variant="caption" display="block">
                            Add Photo
                          </Typography>
                        </Box>
                      )}
                    </PhotoUploadBox>
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={!isEditing}
                    />
                    {isEditing && (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ mt: 1, display: "block", cursor: "pointer" }}
                      >
                        Click to change photo
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Editable Fields based on user.is_editable */}
                <Grid item xs={12} sm={6}>
                  <LockedField>
                    {!user?.is_editable && (
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                    )}
                    <TextField
                      fullWidth
                      label="Owner Name"
                      name="ownerName"
                      value={formik.values.ownerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={!isEditing || !user?.is_editable}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      error={
                        formik.touched.ownerName &&
                        Boolean(formik.errors.ownerName)
                      }
                      helperText={
                        formik.touched.ownerName && formik.errors.ownerName
                      }
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LockedField>
                    {!user?.is_editable && (
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                    )}
                    <TextField
                      fullWidth
                      label="Father's Name"
                      name="fatherName"
                      value={formik.values.fatherName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={!isEditing || !user?.is_editable}
                      error={
                        formik.touched.fatherName &&
                        Boolean(formik.errors.fatherName)
                      }
                      helperText={
                        formik.touched.fatherName && formik.errors.fatherName
                      }
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LockedField>
                    <LockBadge>
                      <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                    </LockBadge>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={user?.email}
                      disabled
                    />
                  </LockedField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LockedField>
                    {!user?.is_editable && (
                      <LockBadge>
                        <LockIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </LockBadge>
                    )}
                    <TextField
                      fullWidth
                      label="CNIC Number"
                      name="cnic"
                      value={formik.values.cnic}
                      onChange={handleCnicChange("cnic")}
                      onBlur={formik.handleBlur}
                      disabled={!isEditing || !user?.is_editable}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      error={
                        formik.touched.cnic && Boolean(formik.errors.cnic)
                      }
                      helperText={formik.touched.cnic && formik.errors.cnic}
                    />
                  </LockedField>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* CNIC Documents */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  CNIC Documents
                </Typography>
                {isEditing && user?.is_editable ? (
                  <Chip label="Editable" size="small" color="primary" />
                ) : (
                  <Chip
                    label="Verified & Locked"
                    size="small"
                    color="warning"
                    icon={<LockIcon sx={{ fontSize: 16 }} />}
                  />
                )}
              </SectionHeader>

              {isEditing && user?.is_editable ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        CNIC Front Side
                      </Typography>
                      {cnicFrontFile ? (
                        <Box
                          component="img"
                          src={URL.createObjectURL(cnicFrontFile)}
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "contain",
                            mb: 2,
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleImageClick(
                              URL.createObjectURL(cnicFrontFile),
                              "CNIC Front"
                            )
                          }
                        />
                      ) : user?.cnicFront ? (
                        <Box
                          component="img"
                          src={user.cnicFront}
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "contain",
                            mb: 2,
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleImageClick(user.cnicFront, "CNIC Front")
                          }
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            mb: 2,
                          }}
                        >
                          <CreditCardIcon sx={{ fontSize: 60, color: "#ccc" }} />
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        ref={cnicFrontInputRef}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error(
                                "Image size must be less than 2MB",
                                { position: "top-center" }
                              );
                              return;
                            }
                            setCnicFrontFile(file);
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CameraAltIcon />}
                        onClick={() => cnicFrontInputRef.current.click()}
                      >
                        {cnicFrontFile || user?.cnicFront
                          ? "Change Front"
                          : "Upload Front"}
                      </Button>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        CNIC Back Side
                      </Typography>
                      {cnicBackFile ? (
                        <Box
                          component="img"
                          src={URL.createObjectURL(cnicBackFile)}
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "contain",
                            mb: 2,
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleImageClick(
                              URL.createObjectURL(cnicBackFile),
                              "CNIC Back"
                            )
                          }
                        />
                      ) : user?.cnicBack ? (
                        <Box
                          component="img"
                          src={user.cnicBack}
                          sx={{
                            width: "100%",
                            maxHeight: 150,
                            objectFit: "contain",
                            mb: 2,
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleImageClick(user.cnicBack, "CNIC Back")
                          }
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            mb: 2,
                          }}
                        >
                          <CreditCardIcon sx={{ fontSize: 60, color: "#ccc" }} />
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        ref={cnicBackInputRef}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error(
                                "Image size must be less than 2MB",
                                { position: "top-center" }
                              );
                              return;
                            }
                            setCnicBackFile(file);
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CameraAltIcon />}
                        onClick={() => cnicBackInputRef.current.click()}
                      >
                        {cnicBackFile || user?.cnicBack
                          ? "Change Back"
                          : "Upload Back"}
                      </Button>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  {/* CNIC Front */}
                  <Grid item xs={12} sm={6}>
                    <DocumentCard
                      onClick={() =>
                        handleImageClick(user?.cnicFront, "CNIC Front")
                      }
                    >
                      <CardContent sx={{ textAlign: "center", p: 2 }}>
                        <BadgeIcon
                          sx={{ fontSize: 40, color: "var(--owner-primary)", mb: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                          CNIC Front
                        </Typography>
                        {user?.cnicFront ? (
                          <CardMedia
                            component="img"
                            height="150"
                            image={user.cnicFront}
                            alt="CNIC Front"
                            sx={{
                              borderRadius: 1,
                              objectFit: "cover",
                              border: "2px solid #e0e0e0",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 150,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#f5f5f5",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No document
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                          mt={1}
                        >
                          Click to view
                        </Typography>
                      </CardContent>
                    </DocumentCard>
                  </Grid>

                  {/* CNIC Back */}
                  <Grid item xs={12} sm={6}>
                    <DocumentCard
                      onClick={() =>
                        handleImageClick(user?.cnicBack, "CNIC Back")
                      }
                    >
                      <CardContent sx={{ textAlign: "center", p: 2 }}>
                        <BadgeIcon
                          sx={{ fontSize: 40, color: "var(--owner-primary)", mb: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                          CNIC Back
                        </Typography>
                        {user?.cnicBack ? (
                          <CardMedia
                            component="img"
                            height="150"
                            image={user.cnicBack}
                            alt="CNIC Back"
                            sx={{
                              borderRadius: 1,
                              objectFit: "cover",
                              border: "2px solid #e0e0e0",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 150,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#f5f5f5",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No document
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                          mt={1}
                        >
                          Click to view
                        </Typography>
                      </CardContent>
                    </DocumentCard>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </StyledCard>

          {/* Contact Information */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <PhoneIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Contact Information
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    name="contactNumber"
                    value={formik.values.contactNumber}
                    onChange={handlePhoneChange("contactNumber")}
                    onBlur={formik.handleBlur}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                    error={
                      formik.touched.contactNumber &&
                      Boolean(formik.errors.contactNumber)
                    }
                    helperText={
                      formik.touched.contactNumber &&
                      formik.errors.contactNumber
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Alternate Number"
                    name="alternateNumber"
                    value={formik.values.alternateNumber}
                    onChange={handlePhoneChange("alternateNumber")}
                    onBlur={formik.handleBlur}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                    error={
                      formik.touched.alternateNumber &&
                      Boolean(formik.errors.alternateNumber)
                    }
                    helperText={
                      formik.touched.alternateNumber &&
                      formik.errors.alternateNumber
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Address Information */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <HomeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Address Information
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Address"
                    name="currentAddress"
                    multiline
                    rows={2}
                    value={formik.values.currentAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!isEditing}
                    error={
                      formik.touched.currentAddress &&
                      Boolean(formik.errors.currentAddress)
                    }
                    helperText={
                      formik.touched.currentAddress &&
                      formik.errors.currentAddress
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Permanent Address"
                    name="permanentAddress"
                    multiline
                    rows={2}
                    value={formik.values.permanentAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!isEditing}
                    error={
                      formik.touched.permanentAddress &&
                      Boolean(formik.errors.permanentAddress)
                    }
                    helperText={
                      formik.touched.permanentAddress &&
                      formik.errors.permanentAddress
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Occupation & Income */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <WorkIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Occupation & Income
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    disabled={!isEditing}
                    error={
                      formik.touched.occupation &&
                      Boolean(formik.errors.occupation)
                    }
                  >
                    <InputLabel>Occupation</InputLabel>
                    <Select
                      name="occupation"
                      value={formik.values.occupation}
                      label="Occupation"
                      onChange={handleOccupationChange}
                      onBlur={formik.handleBlur}
                    >
                      {occupationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.occupation && formik.errors.occupation && (
                      <FormHelperText>
                        {formik.errors.occupation}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    disabled={!isEditing}
                    error={
                      formik.touched.sourceOfIncome &&
                      Boolean(formik.errors.sourceOfIncome)
                    }
                  >
                    <InputLabel>Source of Income</InputLabel>
                    <Select
                      name="sourceOfIncome"
                      value={formik.values.sourceOfIncome}
                      label="Source of Income"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {getSourceOfIncomeOptionsFor(formik.values.occupation).map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {formik.touched.sourceOfIncome &&
                      formik.errors.sourceOfIncome && (
                        <FormHelperText>
                          {formik.errors.sourceOfIncome}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Monthly Income</InputLabel>
                    <Select
                      name="monthlyIncome"
                      value={formik.values.monthlyIncome}
                      label="Monthly Income"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <MenuItem value="">
                        <em>Select (Optional)</em>
                      </MenuItem>
                      {monthlyIncomeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Digital Signature Section */}
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <SectionHeader>
                <DrawIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Digital Signature
                </Typography>
                {isEditing && user?.is_editable ? (
                  <Chip label="Editable" size="small" color="primary" />
                ) : (
                  <Chip label="View Only" size="small" />
                )}
              </SectionHeader>
              <Box sx={{ textAlign: "center", py: 2 }}>
                {isEditing && user?.is_editable ? (
                  <SignatureCapture
                    label="Your Digital Signature"
                    existingSignature={signature || user?.signature}
                    onSignatureChange={(sig) => setSignature(sig)}
                    error={false}
                    helperText="Please sign in the box above. This signature will be used in agreements."
                    primaryColor="var(--owner-primary)"
                  />
                ) : user?.signature ? (
                  <>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxWidth: 300,
                        mx: "auto",
                        bgcolor: "#f5fff5",
                        borderColor: "#4caf50",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 20 }}
                        />
                        <Typography variant="subtitle2" color="success.main">
                          Signature Captured
                        </Typography>
                      </Box>
                      <Box
                        component="img"
                        src={user.signature}
                        alt="Your Signature"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 100,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          bgcolor: "white",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleImageClick(user.signature, "Digital Signature")
                        }
                      />
                    </Paper>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 2, display: "block" }}
                    >
                      This signature was captured during registration and is
                      used for all your agreements.
                    </Typography>
                  </>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      maxWidth: 300,
                      mx: "auto",
                      bgcolor: "#fff3e0",
                      borderColor: "#ff9800",
                    }}
                  >
                    <DrawIcon
                      sx={{
                        fontSize: 40,
                        color: "#ff9800",
                        opacity: 0.7,
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No signature on file
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Please complete your registration to add your digital
                      signature.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </StyledCard>

          <Box sx={{ mb: 3 }}>
            <InstallAppCard />
          </Box>
        </Container>
      </div>

      {/* Image Modal */}
      <ImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        imageUrl={modalImage.url}
        title={modalImage.title}
      />

      <ToastContainer />
    </>
  );
}